import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCOP, formatDate } from '@/lib/utils';
import { Building2, ExternalLink } from 'lucide-react';
import { PeriodFilter, usePeriod } from '@/components/PeriodFilter';

const Stat = ({ label, value, cls, testId }) => (
  <div className="p-3 lg:p-4 bg-[#16302B] rounded-xl border border-white/5">
    <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
    <p data-testid={testId} className={`font-outfit text-lg lg:text-xl font-semibold tabular-nums mt-1 ${cls}`}>{value}</p>
  </div>
);

const AgentTable = ({ rows, prefix }) => (
  <div className="overflow-x-auto -mx-4 lg:mx-0">
    <table className="w-full text-sm min-w-[520px]">
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
          <th className="text-left py-2 px-4">Agente</th><th className="text-right py-2 px-2">Negocios</th>
          <th className="text-right py-2 px-2">Generado</th><th className="text-right py-2 px-2">Pagado</th><th className="text-right py-2 px-4">Pendiente</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(a => (
          <tr key={a.name} data-testid={`${prefix}-agent-${a.name}`} className="border-b border-white/5">
            <td className="py-2.5 px-4 text-white font-medium">{a.name}</td>
            <td className="py-2.5 px-2 text-right tabular-nums text-white/60">{a.deals}</td>
            <td className="py-2.5 px-2 text-right tabular-nums text-[#A38560]">{formatCOP(a.generated)}</td>
            <td className="py-2.5 px-2 text-right tabular-nums text-[#6BCB77]">{formatCOP(a.paid)}</td>
            <td className="py-2.5 px-4 text-right tabular-nums text-white/60">{formatCOP(Math.max(a.generated - a.paid, 0))}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-white/30">Sin negocios en el periodo</td></tr>}
      </tbody>
    </table>
  </div>
);

const PaymentsTable = ({ rows, prefix }) => (
  <div className="overflow-x-auto -mx-4 lg:mx-0">
    <table className="w-full text-sm min-w-[720px]">
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
          <th className="text-left py-2 px-4">Fecha</th><th className="text-left py-2 px-2">Inmueble</th><th className="text-left py-2 px-2">Brokers</th>
          <th className="text-center py-2 px-2">Deeplook</th><th className="text-right py-2 px-2">Valor</th><th className="text-right py-2 px-4">Negocio</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(p => (
          <tr key={p.id} data-testid={`${prefix}-payment-${p.id}`} className="border-b border-white/5">
            <td className="py-2.5 px-4 text-white/70 tabular-nums whitespace-nowrap">{formatDate(p.date)}</td>
            <td className="py-2.5 px-2 text-white max-w-[220px] truncate">{p.property_address || '—'}</td>
            <td className="py-2.5 px-2 text-white/70">{p.brokers.join(' · ')}</td>
            <td className="py-2.5 px-2 text-center">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.deeplook_linked ? 'bg-purple-400/15 text-purple-300' : 'bg-white/5 text-white/40'}`}>{p.deeplook_linked ? 'Sí' : 'No'}</span>
            </td>
            <td className="py-2.5 px-2 text-right tabular-nums font-medium text-[#6BCB77]">{formatCOP(p.amount)}</td>
            <td className="py-2.5 px-4 text-right">
              <Link to={`/negocios/${p.deal_id}`} data-testid={`${prefix}-payment-link-${p.id}`} className="inline-flex items-center gap-1 text-xs text-[#A38560] hover:text-[#C4A87C] transition-colors">Ver <ExternalLink size={12} /></Link>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-white/30">Sin pagos registrados</td></tr>}
      </tbody>
    </table>
  </div>
);

export const CommissionSummary = ({ target, title, subtitle }) => {
  const { period, setPeriod, query } = usePeriod();
  const [data, setData] = useState(null);
  const prefix = `comm-${target}`;

  useEffect(() => {
    if (!query) return;
    api.get(`/dashboard/commission-summary?target=${target}&${query}`).then(setData).catch(() => {});
  }, [target, query]);

  const accent = target === 'deeplook' ? 'text-purple-400' : 'text-[#A38560]';
  return (
    <div data-testid={`${prefix}-section`} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2"><Building2 size={18} strokeWidth={1.5} className={accent} />{title}</h2>
          <p className="text-xs text-white/40 mt-1">{subtitle}</p>
        </div>
        <PeriodFilter period={period} setPeriod={setPeriod} prefix={prefix} />
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Generado" value={formatCOP(data.generated)} cls={accent} testId={`${prefix}-generated`} />
            <Stat label="Pagado en el periodo" value={formatCOP(data.paid)} cls="text-[#6BCB77]" testId={`${prefix}-paid`} />
            <Stat label="Pendiente" value={formatCOP(Math.max(data.generated - data.paid, 0))} cls="text-white" testId={`${prefix}-pending`} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white mb-2">Aporte por agente <span className="text-white/30 font-normal">(negocios compartidos se dividen 50/50)</span></h3>
            <AgentTable rows={data.by_agent} prefix={prefix} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white mb-2">Últimos pagos recibidos</h3>
            <PaymentsTable rows={data.recent_payments} prefix={prefix} />
          </div>
        </>
      )}
    </div>
  );
};
